// The wire of the state service as the console's Stores surface speaks it
// (02-DESIGN/10-state/01-stores-types-and-instances.md, 02-the-state-service.md,
// 03-indexes.md; decision 0015): the family's subjects through the one
// builder, the operation record that rides headers, the message-id and
// expected-sequence guard, the store, type and index declaration bodies, the
// frame and watch headers, the error codes, and a client that speaks them
// sealed with the caller's carrier on every request, because the author of
// every operation is read from it. The family is account-local, so no subject
// carries a tenant. The service side — the operation subjects it publishes,
// its subscriptions, the parsers — is the Go library's and the state service's.
import { headers as newHeaders } from "@nats-io/nats-core";
import { utf8Decode, utf8Encode } from "./encoding.js";
import { CODE_CANNOT_OPEN, DEFAULT_TIMEOUT, ServiceError } from "./envelope.js";
import * as frame from "./frame.js";
import { CARRIER_HEADER } from "./identity.js";
import { client, Family, SEP, tokens } from "./subject.js";
import * as watch from "./watch.js";
/** SERVICE is the state service's micro service name, under which callers in the tenant discover its sealing key. */
export const SERVICE = "state";
/** The verbs: the token after the family. */
export const Verb = {
    /** The admin's acts on a store's declarations: `state.store.<act>…`, `state.type.<act>…`, `state.index.<act>…`. */
    store: "store",
    type: "type",
    index: "index",
    /** The member's verbs on an instance: `state.<verb>.<store>.<path…>`. */
    apply: "apply",
    snapshot: "snapshot",
    get: "get",
    history: "history",
    watch: "watch",
    /** Lists the instances of a type, `state.list.<store>.<type>`, or every instance of a store a page at a time, `state.list.<store>`. */
    list: "list",
    /** Reserved in the verb position: the operation subjects the service alone publishes. */
    log: "log",
};
/** The acts under the store, type and index verbs. */
export const Act = {
    create: "create",
    update: "update",
    get: "get",
    list: "list",
    delete: "delete",
    define: "define",
    declare: "declare",
    query: "query",
};
/** OP_SNAPSHOT is the one operation type the platform itself understands: the instance's whole state inline, resetting the fold; a rollup on a compactable store. */
export const OP_SNAPSHOT = "snapshot";
/** RECORD_VERSION is the operation record's envelope version. */
export const RECORD_VERSION = "1";
/** The family's headers. The record's five ride every operation beside Nats-Msg-Id; the rest are the verbs' own. */
export const HEADER_OP = "Impire-State-Op";
export const HEADER_AUTHOR = "Impire-State-Author";
export const HEADER_PARENTS = "Impire-State-Parents";
export const HEADER_TS = "Impire-State-Ts";
export const HEADER_VERSION = "Impire-State-Version";
/** HEADER_EXPECT_SEQ on an apply names the sequence the writer expects on the instance: the server's guard, refused conflict when anything landed since; zero is create-if-absent. */
export const HEADER_EXPECT_SEQ = "Impire-State-Expect-Seq";
/** HEADER_EXPECT_REVISION on a type define guards a revision of the record. */
export const HEADER_EXPECT_REVISION = "Impire-State-Expect-Revision";
/** HEADER_SEQ on a history frame is the operation's stream sequence, the cursor. */
export const HEADER_SEQ = "Impire-State-Seq";
/** HEADER_FROM on a history request is the sequence to read oldest first from; absent, the page is newest first (decision 0062). */
export const HEADER_FROM = "Impire-State-From";
/** HEADER_CHILDREN on a history request, `true`, includes everything under the path, in one order. */
export const HEADER_CHILDREN = "Impire-State-Children";
/** HEADER_TOTAL on a query's terminator is the count of everything that matched. */
export const HEADER_TOTAL = "Impire-State-Total";
/** HEADER_LIMIT on a store-wide list names how many items the page holds: DEFAULT_LIST_LIMIT when absent, at most MAX_LIST_LIMIT. */
export const HEADER_LIMIT = "Impire-State-Limit";
/** HEADER_AFTER on a store-wide list names a path; the page starts after it. */
export const HEADER_AFTER = "Impire-State-After";
/** HEADER_NEXT on a store-wide list's terminator is the path to ask after for the next page, absent on the last; HEADER_TOTAL beside it counts every instance of the store. */
export const HEADER_NEXT = "Impire-State-Next";
/** The store-wide list's page bounds. */
export const DEFAULT_LIST_LIMIT = 200;
export const MAX_LIST_LIMIT = 1000;
/** HEADER_BEFORE on a history request names a sequence; the page is the newest operations before it. HEADER_LIMIT, HEADER_TOTAL and HEADER_NEXT are the list's, read on a history as counts and sequences. */
export const HEADER_BEFORE = "Impire-State-Before";
/** A history page's bounds: DEFAULT_HISTORY_LIMIT operations when no limit is named, at most MAX_HISTORY_LIMIT. */
export const DEFAULT_HISTORY_LIMIT = 100;
export const MAX_HISTORY_LIMIT = 1000;
/** HEADER_INSTANCE rides every history frame: the path of the instance the operation belongs to. */
export const HEADER_INSTANCE = "Impire-State-Instance";
/** The leased watch over an instance's state, with this family's names; the cursor is the state bucket's revision. */
export const HEADER_SNAPSHOT = "Impire-State-Snapshot";
export const HEADER_SINCE = "Impire-State-Since";
export const HEADER_REVISION = "Impire-State-Revision";
/** HEADER_MSG_ID is the operation's id and its dedup key: the server's own header, made once by the writer and kept across retries. */
export const HEADER_MSG_ID = "Nats-Msg-Id";
/** CHILDREN_TRUE is the value of HEADER_CHILDREN that includes the children. */
export const CHILDREN_TRUE = "true";
/** WATCH_HEADERS is the leased watch instantiated with this family's headers. */
export const WATCH_HEADERS = {
    snapshot: HEADER_SNAPSHOT,
    since: HEADER_SINCE,
    revision: HEADER_REVISION,
};
/** Error codes, kebab-case as every family's are. */
export const Code = {
    badRequest: "bad-request",
    unauthorized: "unauthorized",
    failed: "failed",
    cannotOpen: CODE_CANNOT_OPEN,
    /**
     * The chain's identity refusals, the same on every family (decision 0019):
     * no carrier, or one that does not verify. `unauthorized` is a verified
     * caller the service will not act for — a member on an admin's verb.
     */
    identityRequired: "identity-required",
    identityInvalid: "identity-invalid",
    /** The expected-sequence guard's miss, and a type define under a stale revision. */
    conflict: "conflict",
    /** A write that fails the pre-flight against the type. */
    invalid: "invalid",
    /** An append the stream refused at the store's byte cap. */
    budget: "budget",
    /** A store or an index that already exists. */
    exists: "exists",
    /** A store, type, index or instance that does not exist. */
    notFound: "not-found",
    /** A query of an index that has not caught up with the stream head it observed at start. */
    indexNotReady: "index-not-ready",
    /** A snapshot whose state exceeds the inline limit. */
    tooLarge: "too-large",
    /** A rollup on a store or a type whose history is full, and a delete of what cannot be deleted. */
    refused: "refused",
    /** A store asked for copies the install's JetStream cannot place (decision 0050). */
    replicas: "replicas",
};
/** MAX_SNAPSHOT bounds a snapshot's state, inline, in bytes: a state too large for one message is a state too large for this design. */
export const MAX_SNAPSHOT = 128 * 1024;
/** DEFAULT_BUDGET is a store's default size, its byte cap (decision 0050). */
export const DEFAULT_BUDGET = 256 * 1024 * 1024;
/** MIN_BUDGET is the floor of a store's size. */
export const MIN_BUDGET = 1024 * 1024;
/** NEAR_FULL is the fraction of its size past which a store is near full: the console's mark, and where a compact store makes room. */
export const NEAR_FULL = 0.9;
/** The errors of the grammar. */
export class BadNameError extends Error {
    constructor(message = "state: not a name") {
        super(message);
        this.name = "BadNameError";
    }
}
export class ReservedNameError extends Error {
    constructor(message = "state: reserved name") {
        super(message);
        this.name = "ReservedNameError";
    }
}
export class BadPathError extends Error {
    constructor(message = "state: not a path") {
        super(message);
        this.name = "BadPathError";
    }
}
/** validName reports whether s is a name of the grammar: `[a-z0-9-]+`. */
export function validName(s) {
    return /^[a-z0-9-]+$/.test(s);
}
/** RESERVED are the store names refused at creation: the tokens the family's own grammar uses. */
export const RESERVED = ["meta", Verb.log, Verb.index, Verb.store, Verb.type];
/** checkStoreName throws for a store name that is not a name, or is reserved. */
export function checkStoreName(s) {
    if (!validName(s)) {
        throw new BadNameError();
    }
    if (RESERVED.includes(s)) {
        throw new ReservedNameError();
    }
}
/** validStoreName reports whether s is a store name: a name that is not reserved. */
export function validStoreName(s) {
    return validName(s) && !RESERVED.includes(s);
}
/** PATH_SEPARATOR separates a path's tokens in its written form. */
export const PATH_SEPARATOR = "/";
/**
 * A path names an instance: one or more tokens the tenant chooses, each
 * `[a-z0-9-]+`, written with slashes — `invoice/inv-1/comments/c-3` — and
 * carried on the wire as the dotted tail of the instance's exact subject.
 */
export function parsePath(s) {
    if (s === "" || !s.split(PATH_SEPARATOR).every(validName)) {
        throw new BadPathError(`state: not a path: "${s}"`);
    }
    return s;
}
/** pathOf joins tokens into a path, validating each. */
export function pathOf(...toks) {
    return parsePath(toks.join(PATH_SEPARATOR));
}
/** pathTokens are the path's tokens. */
export function pathTokens(path) {
    return path.split(PATH_SEPARATOR);
}
/** tail is the path in its wire form: the tokens joined with dots, the dotted tail of the instance's subject. */
export function tail(path) {
    return path.split(PATH_SEPARATOR).join(SEP);
}
/** pathFromTail reads a path back from its dotted tail. */
export function pathFromTail(t) {
    return parsePath(tokens(t).join(PATH_SEPARATOR));
}
/** pairs reads the path as alternating `<type>/<id>` pairs and answers the type names, or undefined when the path does not have an even number of tokens. */
export function pairs(path) {
    const toks = pathTokens(path);
    if (toks.length % 2 !== 0) {
        return undefined;
    }
    return toks.filter((_, i) => i % 2 === 0);
}
/** typeOf is the first token of a path: the type it names, when it resolves. */
export function typeOf(path) {
    return pathTokens(path)[0] ?? "";
}
/** storeSubject is `state.store.<act>.<store>` for create, update, get and delete. */
export function storeSubject(act, store) {
    return client(Family.state, Verb.store, act, store);
}
/** storeListSubject is `state.store.list`. */
export function storeListSubject() {
    return client(Family.state, Verb.store, Act.list);
}
/** typeSubject is `state.type.<act>.<store>.<type>` for define and get. */
export function typeSubject(act, store, typ) {
    return client(Family.state, Verb.type, act, store, typ);
}
/** typeListSubject is `state.type.list.<store>`. */
export function typeListSubject(store) {
    return client(Family.state, Verb.type, Act.list, store);
}
/** indexSubject is `state.index.<act>.<store>.<index>` for declare, delete and query. */
export function indexSubject(act, store, index) {
    return client(Family.state, Verb.index, act, store, index);
}
/** indexListSubject is `state.index.list.<store>`. */
export function indexListSubject(store) {
    return client(Family.state, Verb.index, Act.list, store);
}
/** instanceSubject is `state.<verb>.<store>.<path…>` for apply, snapshot, get, history and watch. */
export function instanceSubject(verb, store, path) {
    return client(Family.state, verb, store, tail(path));
}
/** storeItemsSubject is `state.list.<store>`: every instance of the store, a page at a time. */
export function storeItemsSubject(store) {
    return client(Family.state, Verb.list, store);
}
/** listSubject is `state.list.<store>.<type>`. */
export function listSubject(store, typ) {
    return client(Family.state, Verb.list, store, typ);
}
// The bodies.
/** HistoryPolicy says whether an instance's history may be replaced by a snapshot; compactable is the default. */
export const HistoryPolicy = { compactable: "compactable", full: "full" };
/**
 * WhenFull is what a store does as its history reaches its size (decision
 * 0050): refuse new changes, the default and the only policy of a full store;
 * or compact — make room by keeping only current state.
 */
export const WhenFull = { refuse: "refuse", compact: "compact" };
/** Effect is what an operation does to the state: none lives in the history and moves no state; merge is a JSON merge patch onto the state. */
export const Effect = { none: "none", merge: "merge" };
/** IndexKind names what an index is; search is the first kind. */
export const IndexKind = { search: "search" };
/** IndexSource is which face of the store an index reads: state, the default, or history. */
export const IndexSource = { state: "state", history: "history" };
/** recordHeaders renders the record's headers: the id, the type, the author when set, one value per parent, the clock when set, and the version. */
export function recordHeaders(op, into) {
    const h = into ?? newHeaders();
    if (op.id !== undefined && op.id !== "") {
        h.set(HEADER_MSG_ID, op.id);
    }
    h.set(HEADER_OP, op.type);
    if (op.author !== undefined && op.author !== "") {
        h.set(HEADER_AUTHOR, op.author);
    }
    for (const p of op.parents ?? []) {
        h.append(HEADER_PARENTS, p);
    }
    if (op.ts !== undefined && op.ts !== "") {
        h.set(HEADER_TS, op.ts);
    }
    h.set(HEADER_VERSION, op.version !== undefined && op.version !== "" ? op.version : RECORD_VERSION);
    return h;
}
function parseSeq(s) {
    if (!/^\d+$/.test(s)) {
        return undefined;
    }
    const n = Number(s);
    return Number.isSafeInteger(n) ? n : undefined;
}
/** historyOp reads one history frame: the record, with the instance's path or, absent, the path asked for. */
function historyOp(h, data, path) {
    const op = parseRecord(h, data);
    if (op.path === undefined) {
        op.path = path;
    }
    return op;
}
/** parseRecord reads the record back from a history frame's headers, where HEADER_SEQ carries the sequence and HEADER_INSTANCE the path. */
export function parseRecord(h, data) {
    const op = {
        id: h.get(HEADER_MSG_ID),
        type: h.get(HEADER_OP),
        author: h.get(HEADER_AUTHOR),
        parents: h.has(HEADER_PARENTS) ? h.values(HEADER_PARENTS) : [],
        ts: h.get(HEADER_TS),
        version: h.get(HEADER_VERSION),
        data: utf8Decode(data),
    };
    const seq = parseSeq(h.get(HEADER_SEQ));
    if (seq !== undefined) {
        op.seq = seq;
    }
    const path = h.get(HEADER_INSTANCE);
    if (path !== "") {
        op.path = path;
    }
    return op;
}
/** BadHistoryPageError is a limit that is not a count from 1 to MAX_HISTORY_LIMIT, a cursor that is not a sequence, or both cursors at once. */
export class BadHistoryPageError extends Error {
    constructor(message = "state: a history page is a limit from 1 to 1000 and at most one cursor, a sequence") {
        super(message);
        this.name = "BadHistoryPageError";
    }
}
/** BadPageError is a limit that is not a count from 1 to MAX_LIST_LIMIT. */
export class BadPageError extends Error {
    constructor(message = "state: a page is a limit from 1 to 1000 and an after that is a path") {
        super(message);
        this.name = "BadPageError";
    }
}
/** newId mints an operation id: 96 bits of randomness as base64url-safe hex, unique per writer. */
function newId() {
    const b = new Uint8Array(12);
    crypto.getRandomValues(b);
    let out = "";
    for (const x of b) {
        out += x.toString(16).padStart(2, "0");
    }
    return out;
}
/**
 * Client speaks the family for one caller in the tenant — an admin on the
 * declarations, a member on the instances — every request and reply sealed,
 * the caller's carrier on every request, because the author of every
 * operation is read from it.
 */
export class Client {
    nc;
    caller;
    carrier;
    timeout;
    idle;
    /** A client over a connection with the caller's ephemeral key and the carrier it was minted. */
    constructor(nc, caller, carrier, opts = {}) {
        this.nc = nc;
        this.caller = caller;
        this.carrier = carrier;
        this.timeout = opts.timeout ?? DEFAULT_TIMEOUT;
        this.idle = opts.idle;
    }
    header(extra) {
        const h = newHeaders();
        if (extra) {
            for (const [k, values] of extra) {
                for (const v of values) {
                    h.append(k, v);
                }
            }
        }
        if (this.carrier !== "") {
            h.set(CARRIER_HEADER, this.carrier);
        }
        return h;
    }
    /** request speaks one unary verb and decodes the reply when it has one. */
    async request(subj, body, hdr) {
        const reply = await this.caller.request(this.nc, SERVICE, subj, body ?? new Uint8Array(0), {
            headers: this.header(hdr),
            timeout: this.timeout,
        });
        if (reply.data.length === 0) {
            return undefined;
        }
        try {
            return JSON.parse(utf8Decode(reply.data));
        }
        catch (err) {
            throw new Error(`state: ${subj}: reply is not JSON`, { cause: err });
        }
    }
    requestJSON(subj, body, hdr) {
        return this.request(subj, body === undefined ? undefined : utf8Encode(JSON.stringify(body)), hdr);
    }
    /** open opens a streaming verb with the caller's carrier. */
    open(subj, body, hdr, signal) {
        return frame.open(this.nc, this.caller, {
            service: SERVICE,
            subject: subj,
            body,
            headers: this.header(hdr),
            idle: this.idle,
            signal,
        });
    }
    // The admin's verbs.
    /** createStore creates a store: its stream, its state bucket and its declaration. Refused exists when the name is taken, bad-request when the configuration is outside the install's bounds. */
    async createStore(name, req = {}) {
        checkStoreName(name);
        return (await this.requestJSON(storeSubject(Act.create, name), req));
    }
    /**
     * updateStore changes a store's description, size or policy when full and
     * answers its configuration and fill. Refused invalid for a size below the
     * fill or compact on a full store, budget for a size the room cannot reserve.
     */
    async updateStore(name, req) {
        checkStoreName(name);
        return (await this.requestJSON(storeSubject(Act.update, name), req));
    }
    /** getStore reads a store's configuration and fill. */
    async getStore(name) {
        checkStoreName(name);
        return (await this.request(storeSubject(Act.get, name), undefined));
    }
    /** listStores is every store of the tenant. */
    async listStores() {
        const reply = await this.request(storeListSubject(), undefined);
        return reply?.stores ?? [];
    }
    /** deleteStore deletes a store for good: its stream, its bucket and its declarations. */
    async deleteStore(name) {
        checkStoreName(name);
        await this.request(storeSubject(Act.delete, name), undefined);
    }
    /**
     * defineType sets a type whole. expected, when given, is the revision the
     * record was read at, and the define is refused conflict when anything
     * landed since. The reply is the record with its new revision.
     */
    async defineType(store, typ, rec, expected) {
        checkStoreName(store);
        if (!validName(typ)) {
            throw new BadNameError();
        }
        let hdr;
        if (expected !== undefined) {
            hdr = newHeaders();
            hdr.set(HEADER_EXPECT_REVISION, String(expected));
        }
        const body = { ...rec };
        delete body.revision;
        return (await this.requestJSON(typeSubject(Act.define, store, typ), body, hdr));
    }
    /** getType reads a type's record. */
    async getType(store, typ) {
        checkStoreName(store);
        if (!validName(typ)) {
            throw new BadNameError();
        }
        return (await this.request(typeSubject(Act.get, store, typ), undefined));
    }
    /** listTypes is every type of a store. */
    async listTypes(store) {
        checkStoreName(store);
        const reply = await this.request(typeListSubject(store), undefined);
        return reply?.types ?? [];
    }
    /** declareIndex declares an index, create-if-absent: the loser of two racing declares is told exists. */
    async declareIndex(store, index, decl) {
        checkStoreName(store);
        if (!validName(index)) {
            throw new BadNameError();
        }
        return (await this.requestJSON(indexSubject(Act.declare, store, index), decl));
    }
    /** deleteIndex removes a declaration; the index is dropped. */
    async deleteIndex(store, index) {
        checkStoreName(store);
        if (!validName(index)) {
            throw new BadNameError();
        }
        await this.request(indexSubject(Act.delete, store, index), undefined);
    }
    /** listIndexes is every index of a store. */
    async listIndexes(store) {
        checkStoreName(store);
        const reply = await this.request(indexListSubject(store), undefined);
        return reply?.indexes ?? [];
    }
    // The member's verbs.
    /**
     * apply appends one operation to the instance and returns where it landed.
     * The id is minted when the caller left it empty and kept across the
     * caller's retries by handing the same operation back. expected, when given,
     * arms the expected-sequence guard: the write is refused conflict when
     * anything landed on the instance since; zero is create-if-absent.
     *
     * The guard fires before dedup, so a retried guarded write whose first
     * attempt landed is refused conflict too. After a guard refusal the client
     * judges: it reads the instance's history from the sequence after the
     * expected one, and when the first operation there carries this id the
     * write landed and its sequence is returned; otherwise the instance moved,
     * and the conflict stands — the caller re-reads, re-validates and retries.
     */
    async apply(store, path, op, expected) {
        checkStoreName(store);
        parsePath(path);
        if (op.type === "") {
            throw new ServiceError(Code.badRequest, "an operation has a type");
        }
        if (op.type === OP_SNAPSHOT && utf8Encode(op.data).length > MAX_SNAPSHOT) {
            throw new ServiceError(Code.tooLarge, `a snapshot is at most ${MAX_SNAPSHOT} bytes`);
        }
        if (op.id === undefined || op.id === "") {
            op.id = newId();
        }
        if (op.ts === undefined || op.ts === "") {
            op.ts = new Date().toISOString();
        }
        const hdr = recordHeaders(op);
        if (expected !== undefined) {
            hdr.set(HEADER_EXPECT_SEQ, String(expected));
        }
        try {
            const reply = await this.request(instanceSubject(Verb.apply, store, path), utf8Encode(op.data), hdr);
            return { id: op.id, seq: reply?.seq ?? 0 };
        }
        catch (err) {
            if (expected === undefined || !(err instanceof ServiceError) || err.code !== Code.conflict) {
                throw err;
            }
            const landed = await this.landed(store, path, op.id, expected + 1);
            if (landed !== undefined) {
                return { id: op.id, seq: landed };
            }
            throw err;
        }
    }
    /** landed reads the first operation on the instance from the sequence after the guard and answers its sequence when it is the one with id. */
    async landed(store, path, id, from) {
        try {
            for await (const op of this.history(store, path, { from })) {
                return op.id === id ? (op.seq ?? 0) : undefined;
            }
        }
        catch {
            return undefined;
        }
        return undefined;
    }
    /** snapshot asks the service to write a snapshot of the instance's current state: a rollup on a compactable store, refused on a full one. */
    async snapshot(store, path) {
        checkStoreName(store);
        parsePath(path);
        const reply = await this.request(instanceSubject(Verb.snapshot, store, path), undefined);
        return reply?.seq ?? 0;
    }
    /** get reads the instance's folded state and the sequence it was folded to. */
    async get(store, path) {
        checkStoreName(store);
        parsePath(path);
        return (await this.request(instanceSubject(Verb.get, store, path), undefined));
    }
    /**
     * history streams the instance's operations oldest first from a sequence —
     * absent is the start — to the stream head; with children, every operation
     * under the path too, in one order. Each operation carries its sequence and
     * its path. The service answers a page at a time (decision 0062), and
     * history asks for the next page from the terminator's continuation until
     * there is none, so a reader still receives everything. The iterator ends
     * with the last page, or throws what ended it.
     */
    async *history(store, path, opts = {}) {
        checkStoreName(store);
        parsePath(path);
        let from = opts.from !== undefined && opts.from > 0 ? opts.from : 1;
        for (;;) {
            const r = await this.openHistory(store, path, {
                from,
                limit: MAX_HISTORY_LIMIT,
                children: opts.children,
                signal: opts.signal,
            });
            for await (const f of r.frames()) {
                if (f.kind !== frame.Kind.content) {
                    continue;
                }
                yield historyOp(f.headers, f.data, path);
            }
            const next = parseSeq(r.terminator()?.get(HEADER_NEXT) ?? "");
            if (next === undefined || next <= from) {
                return;
            }
            from = next;
        }
    }
    /**
     * historyPage reads one page of the instance's history: newest first by
     * default or before `before`, oldest first from `from`, at most `limit`;
     * the total and the continuation the terminator carried. A newest-first
     * page continues with `{ before: next }`, a forward one with `{ from: next }`.
     */
    async historyPage(store, path, opts = {}) {
        checkStoreName(store);
        parsePath(path);
        const r = await this.openHistory(store, path, opts);
        const out = { ops: [], total: 0 };
        for await (const f of r.frames()) {
            if (f.kind !== frame.Kind.content) {
                continue;
            }
            out.ops.push(historyOp(f.headers, f.data, path));
        }
        const t = r.terminator();
        out.total = parseSeq(t?.get(HEADER_TOTAL) ?? "") ?? 0;
        const next = parseSeq(t?.get(HEADER_NEXT) ?? "");
        if (next !== undefined && next > 0) {
            out.next = next;
        }
        return out;
    }
    /** openHistory opens one page of `state.history`, refusing what the service would. */
    async openHistory(store, path, opts) {
        const seq = (n) => n === undefined || (Number.isSafeInteger(n) && n > 0);
        if (!seq(opts.before) || !seq(opts.from) || (opts.before !== undefined && opts.from !== undefined)) {
            throw new BadHistoryPageError();
        }
        if (opts.limit !== undefined &&
            (!Number.isInteger(opts.limit) || opts.limit < 1 || opts.limit > MAX_HISTORY_LIMIT)) {
            throw new BadHistoryPageError();
        }
        const hdr = newHeaders();
        if (opts.from !== undefined) {
            hdr.set(HEADER_FROM, String(opts.from));
        }
        if (opts.before !== undefined) {
            hdr.set(HEADER_BEFORE, String(opts.before));
        }
        if (opts.limit !== undefined) {
            hdr.set(HEADER_LIMIT, String(opts.limit));
        }
        if (opts.children === true) {
            hdr.set(HEADER_CHILDREN, CHILDREN_TRUE);
        }
        return this.open(instanceSubject(Verb.history, store, path), undefined, hdr, opts.signal);
    }
    /**
     * watch follows the instance's state as the leased watch: its current
     * `{seq, state}` as the snapshot, its completion, then every fold as a
     * change, the lease reopened underneath so the caller never sees the seam.
     * The signal ends it; an error ends it too and is the caller's.
     */
    watch(store, path, opts = {}) {
        checkStoreName(store);
        parsePath(path);
        return watch.open(this.nc, this.caller, {
            service: SERVICE,
            subject: instanceSubject(Verb.watch, store, path),
            headers: this.header(),
            watchHeaders: WATCH_HEADERS,
            idle: this.idle,
            signal: opts.signal,
        }, (data) => JSON.parse(utf8Decode(data)));
    }
    /** list streams the instances of a type with their state; a field filter is the caller's. */
    async *list(store, typ, opts = {}) {
        checkStoreName(store);
        if (!validName(typ)) {
            throw new BadNameError();
        }
        const r = await this.open(listSubject(store, typ), undefined, undefined, opts.signal);
        for await (const f of r.frames()) {
            if (f.kind !== frame.Kind.content) {
                continue;
            }
            yield JSON.parse(utf8Decode(f.data));
        }
    }
    /**
     * listItems reads one page of every instance of a store, typed or not, in
     * path order, each with its type and its last change — never its state:
     * the items after `after` (from the start when absent), at most `limit`
     * (DEFAULT_LIST_LIMIT when absent), the total and the next cursor the
     * terminator carried.
     */
    async listItems(store, opts = {}) {
        checkStoreName(store);
        const hdr = newHeaders();
        if (opts.after !== undefined && opts.after !== "") {
            parsePath(opts.after);
            hdr.set(HEADER_AFTER, opts.after);
        }
        if (opts.limit !== undefined) {
            if (!Number.isInteger(opts.limit) || opts.limit < 1 || opts.limit > MAX_LIST_LIMIT) {
                throw new BadPageError();
            }
            hdr.set(HEADER_LIMIT, String(opts.limit));
        }
        const r = await this.open(storeItemsSubject(store), undefined, hdr, opts.signal);
        const out = { items: [], total: 0 };
        for await (const f of r.frames()) {
            if (f.kind !== frame.Kind.content) {
                continue;
            }
            out.items.push(JSON.parse(utf8Decode(f.data)));
        }
        const t = r.terminator();
        const total = parseSeq(t?.get(HEADER_TOTAL) ?? "");
        if (total !== undefined) {
            out.total = total;
        }
        const next = t?.get(HEADER_NEXT) ?? "";
        if (next !== "") {
            out.next = next;
        }
        return out;
    }
    /**
     * search queries a search index and collects the streamed reply: the hits,
     * best first, under the query's limit, and the total the terminator
     * carried. Refused index-not-ready while the index is rebuilding.
     */
    async search(store, index, q, opts = {}) {
        checkStoreName(store);
        if (!validName(index)) {
            throw new BadNameError();
        }
        const r = await this.open(indexSubject(Act.query, store, index), utf8Encode(JSON.stringify(q)), undefined, opts.signal);
        const out = { hits: [], total: 0 };
        for await (const f of r.frames()) {
            if (f.kind !== frame.Kind.content) {
                continue;
            }
            const hits = JSON.parse(utf8Decode(f.data));
            out.hits.push(...hits);
        }
        const total = parseSeq(r.terminator()?.get(HEADER_TOTAL) ?? "");
        if (total !== undefined) {
            out.total = total;
        }
        return out;
    }
}
//# sourceMappingURL=state.js.map